import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { networks, preferredNetwork } from '../config/settings';
import { findNetworkByChainId, toHexChainId } from '../utils/chainId';
import {
  getInjectedProvider,
  getWalletOptions,
  selectProvider,
  waitForProvider,
  onProvidersChanged,
} from '../utils/walletProvider';
import WalletPickerModal from './WalletPickerModal';

const friendlyWalletError = (error) => {
  if (!error) return 'Could not connect to wallet.';
  if (error.code === -32002) {
    return 'A wallet request is already pending. Open your wallet extension to continue.';
  }
  if (error.code === -32603) {
    return 'Your wallet returned an internal error. Make sure it is unlocked and finished setup, then try again.';
  }
  return `Could not connect to wallet. ${error.message || ''}`.trim();
};

const ConnectButton = forwardRef(({ handleConnectionStatus }, ref) => {
  const [account, setAccount] = useState(localStorage.getItem('connectedAccount') || '');
  const [currentNetwork, setCurrentNetwork] = useState(null);
  const [walletChainId, setWalletChainId] = useState(null);
  const [networkMismatch, setNetworkMismatch] = useState(false);
  const [connectionError, setConnectionError] = useState('');
  const [pickerOptions, setPickerOptions] = useState([]);
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let activeProvider = null;

    const handleChainChanged = (chainId) => {
      // Refresh network state in place. Data components re-read on their own
      // chainChanged listeners, so a full page reload (which some wallets would
      // trigger repeatedly on connect) is unnecessary and caused reload loops.
      updateNetworkState(chainId);
    };

    const bind = (provider) => {
      if (cancelled || !provider || activeProvider === provider) return;
      // Re-bind cleanly if a different wallet becomes active (multi-wallet).
      if (activeProvider) {
        activeProvider.removeListener('accountsChanged', handleAccountsChanged);
        activeProvider.removeListener('chainChanged', handleChainChanged);
      }
      activeProvider = provider;

      if (localStorage.getItem('walletDisconnected') !== 'true') {
        provider
          .request({ method: 'eth_accounts' })
          .then((accounts) => {
            if (cancelled) return;
            handleAccountsChanged(accounts);
            if (accounts.length > 0) {
              provider
                .request({ method: 'eth_chainId' })
                .then((chainId) => {
                  if (!cancelled) updateNetworkState(chainId);
                })
                .catch(() => {});
            }
          })
          .catch(console.error);
      }

      provider.on('accountsChanged', handleAccountsChanged);
      provider.on('chainChanged', handleChainChanged);
    };

    // Discover the wallet via EIP-6963 (Uniswap, MetaMask, Coinbase, Rabby, ...)
    // with a legacy `window.ethereum` fallback. Wallets inject asynchronously,
    // so wait for one rather than giving up on first paint.
    waitForProvider().then((provider) => {
      if (provider) bind(provider);
    });
    // Re-bind if a wallet announces itself later or the user switches wallets.
    const offProviders = onProvidersChanged(() => {
      const provider = getInjectedProvider();
      if (provider) bind(provider);
    });

    return () => {
      cancelled = true;
      offProviders();
      if (activeProvider) {
        activeProvider.removeListener('accountsChanged', handleAccountsChanged);
        activeProvider.removeListener('chainChanged', handleChainChanged);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateNetworkState = (chainId) => {
    setWalletChainId(toHexChainId(chainId));
    const match = findNetworkByChainId(chainId);

    if (match) {
      setCurrentNetwork(match[0]);
      setNetworkMismatch(false);
      setConnectionError('');
    } else {
      setCurrentNetwork(null);
      const connectedAccount = localStorage.getItem('connectedAccount');
      if (connectedAccount) {
        setNetworkMismatch(true);
        setConnectionError('Please switch your wallet to Base Mainnet to use this app.');
      }
    }
  };

  const handleAccountsChanged = (accounts) => {
    if (accounts.length === 0) {
      console.log('Please connect to your wallet.');
      setAccount('');
      localStorage.removeItem('connectedAccount');
      setNetworkMismatch(false); // Clear network mismatch when disconnected
      setConnectionError(''); // Clear errors when disconnected
      handleConnectionStatus(false);
    } else {
      setAccount(accounts[0]);
      localStorage.setItem('connectedAccount', accounts[0]);
      localStorage.removeItem('walletDisconnected'); // Remove the disconnect flag
      handleConnectionStatus(true);
    }
  };

  // Entry point for the "Connect Wallet" button. Decides whether to connect
  // directly (one wallet) or let the user choose (multiple wallets installed).
  const connectWallet = async () => {
    setConnectionError('');
    // Ensure discovery has had a chance to run (wallets inject asynchronously).
    await waitForProvider();
    const options = getWalletOptions();

    if (options.length === 0) {
      setPickerOptions([]);
      setShowPicker(true);
      return;
    }
    if (options.length === 1) {
      connectToProvider(options[0]);
      return;
    }
    // Multiple wallets: never assume — ask the user which one.
    setPickerOptions(options);
    setShowPicker(true);
  };

  useImperativeHandle(ref, () => ({ connect: connectWallet }));

  const handlePick = (option) => {
    setShowPicker(false);
    connectToProvider(option);
  };

  const connectToProvider = async (option) => {
    const { provider, id } = option;
    if (!provider) return;
    selectProvider(provider, id);
    try {
      const accounts = await provider.request({ method: 'eth_requestAccounts' });
      handleAccountsChanged(accounts);

      // After connecting, check if we're on Base network and switch if needed
      const chainId = await provider.request({ method: 'eth_chainId' });
      updateNetworkState(chainId);

      if (!findNetworkByChainId(chainId)) {
        await switchToBaseNetwork();
      }
    } catch (error) {
      if (error.code === 4001) {
        // User rejected — not an error worth shouting about.
        console.log('Connection request rejected by user.');
        setConnectionError('');
      } else {
        console.error(error);
        setConnectionError(friendlyWalletError(error));
      }
    }
  };

  const switchToBaseNetwork = async () => {
    const baseNetwork = preferredNetwork;
    const provider = getInjectedProvider();
    if (!baseNetwork || !provider) {
      setConnectionError('Unable to switch network. Please select Base Mainnet in your wallet.');
      return;
    }
    try {
      await provider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: baseNetwork.chainId }],
      });
      setCurrentNetwork(preferredNetwork === networks.base ? 'base' : 'local');
      setNetworkMismatch(false);
      setConnectionError('');
    } catch (switchError) {
      if (switchError.code === 4001) {
        setNetworkMismatch(true);
        setConnectionError('Please switch to Base Mainnet to use this app.');
      } else if (switchError.code === 4902) {
        // Base network not added to wallet, add it
        try {
          await provider.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: baseNetwork.chainId,
              chainName: baseNetwork.chainName,
              rpcUrls: baseNetwork.rpcUrls,
              blockExplorerUrls: baseNetwork.blockExplorerUrls,
              nativeCurrency: baseNetwork.nativeCurrency
            }],
          });
          setCurrentNetwork(preferredNetwork === networks.base ? 'base' : 'local');
          setNetworkMismatch(false);
          setConnectionError('');
        } catch (addError) {
          setNetworkMismatch(true);
          setConnectionError(`Could not add Base network. Error: ${addError.message}`);
        }
      } else {
        setNetworkMismatch(true);
        setConnectionError(`Could not switch to Base network. Error: ${switchError.message}`);
      }
    }
  };

  const switchWallet = async () => {
    // Clear local state first, then reopen the picker so the user can choose a
    // different wallet (or a different account in the same wallet).
    setAccount('');
    localStorage.removeItem('connectedAccount');
    setNetworkMismatch(false);
    setConnectionError('');
    handleConnectionStatus(false);

    await waitForProvider();
    const options = getWalletOptions();
    if (options.length === 0) {
      setConnectionError('No browser wallet detected.');
      return;
    }
    if (options.length === 1) {
      connectToProvider(options[0]);
      return;
    }
    setPickerOptions(options);
    setShowPicker(true);
  };

  const disconnectWallet = () => {
    setAccount('');
    localStorage.removeItem('connectedAccount');
    localStorage.setItem('walletDisconnected', 'true');
    setNetworkMismatch(false); // Clear network mismatch when disconnecting
    setConnectionError(''); // Clear errors when disconnecting
    handleConnectionStatus(false);
  };

  const truncateAddress = (address) => {
    return `${address.slice(0, 6)}...${address.slice(-6)}`;
  };

  const network = currentNetwork ? networks[currentNetwork] : null;
  const baseScanAddressUrl = account ? `https://basescan.org/address/${account}` : '#';
  const networkLabel = network
    ? network.chainName
    : networkMismatch
    ? `Wrong network (${walletChainId || 'unknown'})`
    : 'Not connected';

  return (
    <div className="flex flex-col items-end">
      <div className="flex items-center space-x-2 sm:space-x-3">
        {account === '' ? (
          <button 
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 sm:px-6 py-2 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-semibold shadow-md hover:shadow-lg text-sm sm:text-base" 
            onClick={connectWallet}
          >
            Connect Wallet
          </button>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center space-x-1 bg-gray-100 rounded-lg px-3 py-2 border border-gray-200">
              <span className="text-gray-700 font-mono text-sm">{truncateAddress(account)}</span>
              <a 
                href={baseScanAddressUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-blue-600 transition-colors"
                title="View on BaseScan"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
            
            <div className={`rounded-lg px-3 py-2 text-xs font-semibold ${networkMismatch ? 'bg-yellow-100 border border-yellow-400 text-yellow-800' : 'bg-purple-100 border border-purple-200 text-purple-700'}`}>
              {networkLabel}
            </div>
            
            {networkMismatch && (
              <button 
                className="bg-yellow-500 text-white px-3 py-2 rounded-lg hover:bg-yellow-600 transition-all text-sm font-semibold" 
                onClick={switchToBaseNetwork}
              >
                Switch to Base
              </button>
            )}
            
            <button 
              className="bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition-all text-sm font-semibold border border-gray-200 hidden sm:block" 
              onClick={switchWallet}
            >
              Switch
            </button>
            
            <button 
              className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 transition-all text-sm font-semibold shadow-sm" 
              onClick={disconnectWallet}
            >
              Disconnect
            </button>
          </div>
        )}
      </div>
      {connectionError && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-2">
          <p className="text-red-600 text-xs">{connectionError}</p>
        </div>
      )}
      {showPicker && (
        <WalletPickerModal
          options={pickerOptions}
          onSelect={handlePick}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
});

export default ConnectButton;

